import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Days } from "@/lib/api";
import { ClipboardList, ClipboardCheck, FileHeart, Save } from "lucide-react";

const SECTIONS = [
  {
    key: "inpatient_hp",
    label: "Inpatient H&P",
    hint: "History & Physical for new admits",
    icon: ClipboardList,
    accent: "#0284c7",
    placeholder: "Room 201 - John Doe (new admit)\nRoom 305 - Jane Roe (chest pain w/u)",
  },
  {
    key: "follow_up_consult",
    label: "Patient Follow-Up / Consult",
    hint: "Ongoing follow-ups & consults",
    icon: ClipboardCheck,
    accent: "#6b4f8e",
    placeholder: "Room 202 - K. Adams (HTN f/u)\nRoom 410 - S. Lee (medicine consult)",
  },
  {
    key: "php_intake",
    label: "PHP Medical Intake",
    hint: "Partial Hospitalization new intakes",
    icon: FileHeart,
    accent: "#d96c4e",
    placeholder: "Aisha Khan (medical clearance)\nBrian Zhang (metabolic panel review)",
  },
];

function TaskSection({ section, initial, date, coversPHP }) {
  const [value, setValue] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setValue(initial || "");
    setDirty(false);
  }, [initial]);

  useEffect(() => {
    if (!dirty) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await Days.updateMedicalTasks(date, { [section.key]: value });
        setDirty(false);
      } catch {
        toast.error(`Failed to save ${section.label}.`);
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value, dirty, section.key, section.label, date]);

  const Icon = section.icon;
  const isPhpSection = section.key === "php_intake";

  return (
    <div
      className="flex flex-col bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] rounding-column print-break-avoid"
      data-testid={`medical-task-section-${section.key}`}
    >
      <div className="p-4 bg-[#f8fafc] border-b border-[#e2e8f0] provider-header">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ backgroundColor: `${section.accent}18`, color: section.accent }}
            >
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base text-[#0f172a] leading-tight">
                {section.label}
              </h3>
              <div className="text-[10px] uppercase tracking-widest text-[#475569] mt-0.5">
                {section.hint}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#475569] font-mono">
            {saving ? (
              <>
                <Save className="w-3 h-3 animate-pulse" /> saving…
              </>
            ) : dirty ? (
              <span>unsaved</span>
            ) : (
              <span className="opacity-60">saved</span>
            )}
          </div>
        </div>
        {isPhpSection && !coversPHP && (
          <p className="mt-2 text-[11px] text-[#475569] italic">
            Medical rounder is not marked as PHP cover — this list will still appear in the export.
          </p>
        )}
      </div>
      <div className="p-3 no-print">
        <Textarea
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setDirty(true);
          }}
          placeholder={section.placeholder}
          className="min-h-[160px] text-sm font-mono border-[#e2e8f0] focus-visible:ring-[#0284c7] resize-y"
          data-testid={`medical-task-input-${section.key}`}
        />
      </div>
      {/* Print-friendly rendering (list) */}
      <div className="print-only px-4 pb-4">
        {value.trim() ? (
          <ul className="list-disc pl-6 text-sm space-y-1">
            {value.split("\n").filter((l) => l.trim()).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        ) : (
          <p className="italic text-[#909590] text-sm">(none)</p>
        )}
      </div>
    </div>
  );
}

export default function MedicalRounderPanel({ day, date, providerName }) {
  if (!day || !day.medical_rounder_id) return null;
  const tasks = day.medical_tasks || {};
  return (
    <section className="mt-8" data-testid="medical-rounder-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4 no-print">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
            Medical rounder tasks
          </p>
          <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1">
            {providerName || "Medical Rounder"}
            {day.medical_rounder_covers_php && (
              <span className="ml-3 align-middle inline-block bg-[#d96c4e] text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest">
                PHP Medical Intake
              </span>
            )}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
        {SECTIONS.map((s) => (
          <TaskSection
            key={s.key}
            section={s}
            initial={tasks[s.key] || ""}
            date={date}
            coversPHP={!!day.medical_rounder_covers_php}
          />
        ))}
      </div>
    </section>
  );
}
